import {useEffect, useRef, useState} from 'react';
import isEqual from 'react-fast-compare';

import {useDebounce} from 'ui-library/hooks/useDebounce';
import * as net from 'util/Net';

import {ExecutionPlan} from './Types';

export interface TimingEstimateResult {
  /**
   * true if the estimate is stale and will be refreshed soon.
   */
  stale: boolean;
  /**
   * Last-made timing estimate, or `null` if no estimate has been made.
   */
  executionPlan: ExecutionPlan | null;
}

/**
 * Debounced (500ms) estimate for performing action with the specified data.
 *
 * @param url - a URL to POST that accepts {@link TData} and returns a {@link ExecutionPlan}
 * @param data - the data to POST to the URL, or `null` if no estimate is needed.
 */
export default function useTimingEstimate<TData extends Object>(
  url: string,
  data: TData | null
): TimingEstimateResult {
  const [result, setResult] = useState<TimingEstimateResult>({executionPlan: null, stale: false});
  const dataRef = useRef<TData | null>(data);
  if (!isEqual(data, dataRef.current)) {
    dataRef.current = data;
  }
  const currData = dataRef.current;
  useEffect(() => {
    if (currData == null) {
      setResult({executionPlan: null, stale: false});
    } else {
      setResult((prev) => ({...prev, stale: true}));
    }
  }, [currData]);

  const debouncedData = useDebounce(currData, 500);
  useEffect(() => {
    async function doAsync(): Promise<void> {
      try {
        if (debouncedData == null) {
          setResult({executionPlan: null, stale: false});
        } else {
          const newExecutionPlan: ExecutionPlan = await net.postJSONPromise({url, data: debouncedData});
          if (debouncedData === dataRef.current) {
            setResult({executionPlan: newExecutionPlan, stale: false});
          }
        }
      } catch (e) {
        if (net.isServerRequestError(e)) {
          setResult({executionPlan: null, stale: true});
        }
        throw e;
      }
    }
    doAsync();
  }, [debouncedData, dataRef, url]);
  return result;
}
